import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { AppTextField } from '../../customComponents/AppTextField';
import { AppDropdownField } from '../../customComponents/AppDropdownField';
import { AppGPhoneTextField } from '../../customComponents/AppGPhoneTextField';
import { FormTitle } from '../../customComponents/FormTitle';
import PrimaryButton from '../../components/PrimaryButton';
import Agreement from '../../components/Agreement';
import TermsConsentSheet from '../../components/TermsConsentSheet';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import FormContainer from '../../customComponents/FormContainer';
import { useSignUp } from '../../context/SignUpContext';
import { userSignup, getTerms } from '../../lib/api/user/userApi';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';
import { useDevice } from '../../context/DeviceContext';

// 다국어 텍스트 객체
const signUpStep2Texts = {
  ko: {
    signUpSuccess: '회원가입이 완료되었습니다!',
    signUpFailed: '회원가입에 실패했습니다. 다시 시도해주세요.',
    termsLoadFailed: '약관을 불러오는데 실패했습니다.',
    nameValidationError: '이름은 2글자 이상 입력해주세요',
    countryValidationError: '국가를 선택해주세요',
    paymentCurrencyGuide: '국가 선택에 따라 결제 통화가 자동 적용되며, 국내는 KRW, 국외는 USD로 결제됩니다.'
  },
  en: {
    signUpSuccess: 'Sign up completed successfully!',
    signUpFailed: 'Sign up failed. Please try again.',
    termsLoadFailed: 'Failed to load terms.',
    nameValidationError: 'Please enter at least 2 characters for name',
    countryValidationError: 'Please select country',
    paymentCurrencyGuide: 'Payment currency is automatically applied based on country selection. Domestic: KRW, International: USD.'
  }
};

// SignUpContainer styles are now handled by PageContainerLayout

const FlexPageContainer = styled.div<{ $minHeight: string }>`
  display: flex;
  flex-direction: column;
  min-height: ${({ $minHeight }) => $minHeight};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CompleteButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: auto;
  padding: 24px 0 0 0;
`;

const PaymentGuideText = styled.p`
  font-size: 13px;
  color: #f59e0b;
  margin: 8px 0 0 0;
  line-height: 1.4;
  padding: 8px 12px;
  background-color: #fef3c7;
  border-radius: 6px;
  border-left: 4px solid #f59e0b;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-weight: 500;
`;

function SignUpStep2() {
  const navigate = useNavigate();
  const { signUpData, clearSignUpData } = useSignUp();
  const { login, isLoggedIn } = useUserAuth();
  const { lang } = useLang();
  const device = useDevice();
  const texts = signUpStep2Texts[lang];
  
  // 앱바 높이를 임의로 설정 (모바일: 120px, 웹: 250px)  
  const appbarHeight = device === 'mobile' ? 120 : 250;
  const minHeight = `calc(100vh - ${appbarHeight}px)`;

  // 이미 로그인된 상태라면 홈으로 리다이렉트
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);
  
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [isNameValid, setIsNameValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isCountryValid, setIsCountryValid] = useState(false);
  const [individualAgreements, setIndividualAgreements] = useState<boolean[]>([false, false, false]);
  const [isTermsSheetOpen, setIsTermsSheetOpen] = useState(false);
  const [currentTermsSection, setCurrentTermsSection] = useState('privacy');
  const [termsSection, setTermsSection] = useState<any[]>([]);
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);

  // 약관 데이터 불러오기
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setIsLoadingTerms(true);
        const termLang = lang === 'ko' ? 'KOR' : 'ENG';
        const response = await getTerms({ termLang });
        
        if (response.data.statusCode === 200 && response.data.data) {
          const termsArray = response.data.data as any[]; // 타입 단언으로 배열 처리
          
          // 언어에 따라 인덱스 매핑
          let privacyIndex, serviceIndex;
          if (lang === 'ko') {
            // KOR: 1(privacy), 3(service)
            privacyIndex = termsArray.find((term: any) => term.index === 1);
            serviceIndex = termsArray.find((term: any) => term.index === 3);
          } else {
            // ENG: 2(privacy), 4(service)
            privacyIndex = termsArray.find((term: any) => term.index === 2);
            serviceIndex = termsArray.find((term: any) => term.index === 4);
          }

          const formattedTerms = [
            {
              key: 'privacy',
              title: lang === 'ko' ? '개인정보\n취급방침' : 'Privacy\nPolicy',
              content: privacyIndex?.content || ''
            },
            {
              key: 'terms',
              title: lang === 'ko' ? '서비스\n이용약관' : 'Terms of\nService',
              content: serviceIndex?.content || ''
            }
          ];

          setTermsSection(formattedTerms);
        }
      } catch (error) {
        devError('약관 조회 실패:', error);
        toast.error(texts.termsLoadFailed);
      } finally {
        setIsLoadingTerms(false);
      }
    };

    fetchTerms();
  }, [lang, texts.termsLoadFailed]);

  const countryOptions = [
    { value: 'domestic', label: lang === 'ko' ? '국내' : 'Domestic' },
    { value: 'overseas', label: lang === 'ko' ? '국외' : 'Overseas' },
  ];

  const handleBack = () => {
    navigate(-1); // 이전 페이지로 이동
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setName(e.target.value);
    setIsNameValid(e.target.value.trim().length >= 2); // 이름은 2글자 이상
  };

  const handlePhoneChange = (value: string, isValid: boolean) => {
    setPhone(value);
    setIsPhoneValid(isValid);
  };

  const handleCountryChange = (value: string) => {
    setCountry(value);
    setIsCountryValid(value !== '');
  };

  const handleAgreementChange = (allAgreed: boolean, agreements: boolean[]) => {
    setIndividualAgreements(agreements);
  };

  const handleViewPrivacyPolicy = () => {
    setCurrentTermsSection('privacy');
    setIsTermsSheetOpen(true);
  };

  const handleViewTerms = () => {
    setCurrentTermsSection('terms');
    setIsTermsSheetOpen(true);
  };

  const handleTermsSheetClose = () => {
    setIsTermsSheetOpen(false);
  };

  const handleTermsSectionClick = (sectionKey: string) => {
    setCurrentTermsSection(sectionKey);
  };

  const handleComplete = async () => {
    try {
      devLog('회원가입 시작:', { 
        name, 
        phone, 
        country, 
        email: signUpData.email, 
        password: signUpData.password 
      });

      // 회원가입 API 호출
      const response = await userSignup({
        name: name.trim(),
        email: signUpData.email,
        password: signUpData.password,
        cellphone: phone,
        locationType: country === 'domestic' ? 'DOMESTIC' : 'OVERSEAS'
      });

      toast.success(texts.signUpSuccess);
      
      // 회원가입 응답에서 바로 토큰과 사용자 정보 추출하여 로그인 처리
      if (response && response.data && response.data.statusCode === 200 && response.data.data) {
        const signupData = response.data.data;
        
        devLog('회원가입 응답 데이터:', signupData);
        
        // 응답에서 토큰과 사용자 정보 추출
        const accessToken = signupData.accessToken || '';
        const refreshToken = signupData.refreshToken || '';
        
        if (accessToken) {
          // 서버 응답 데이터 그대로 login 함수에 전달
          const serverResponseData = {
            index: signupData.index,
            email: signupData.email,
            name: signupData.name,
            locationType: signupData.locationType,
            providerType: signupData.providerType,
            isNew: signupData.isNew,
            refreshToken: refreshToken
          };
          
          devLog('로그인 처리 시작:', { accessToken: '존재함', refreshToken: '존재함' });
          
          // Context에 로그인 정보 저장 (login 함수가 토큰 저장 및 Flutter 통신 처리)
          login(serverResponseData, accessToken);
        } else {
          devWarn('회원가입 응답에 액세스 토큰이 없습니다');
          // 토큰이 없으면 로그인 페이지로 이동
          navigate('/login');
          return;
        }
      } else {
        devWarn('회원가입 응답 데이터가 없습니다');
        navigate('/login');
        return;
      }
      
      // 회원가입 데이터 초기화
      clearSignUpData();
      
      // 홈 페이지로 이동
      navigate('/');
    } catch (error: any) {
      devError('회원가입 실패:', error);
      
      // 에러 메시지 파싱
      let errorMessage = texts.signUpFailed;
      
      if (error?.data?.error?.customMessage) {
        errorMessage = error.data.error.customMessage;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const isFormValid = isNameValid && isPhoneValid && isCountryValid && 
                     individualAgreements.every(agreed => agreed) &&
                     name.trim() !== '' && phone.trim() !== '' && country.trim() !== '';

  const agreeContents = [
    {
      title: lang === 'ko' 
        ? '(필수) 개인정보 취급방침 동의' 
        : '(Required) Privacy Policy Agreement',
      onView: handleViewPrivacyPolicy
    },
    {
      title: lang === 'ko' 
        ? '(필수) 서비스 이용약관 수집 동의' 
        : '(Required) Terms of Service Agreement',
      onView: handleViewTerms
    },
    {
      title: lang === 'ko' 
        ? '(필수) 만 14세 이상입니다' 
        : '(Required) I am over 14 years old'
      // onView 없음 - 보기 버튼 표시되지 않음
    }
  ];

  return (
    <CenteredLayout>
      <ProjectAppbar 
        title={lang === 'ko' ? "회원가입" : "Sign Up"}
        showBackButton={true}
        onBack={handleBack}
      />
      <FormContainer>
        <PageContainerLayout>
          <FlexPageContainer $minHeight={minHeight}>
            <ContentContainer>
              <FormGroup>
                <FormTitle>{lang === 'ko' ? "회원정보 입력" : "Enter Member Information"}</FormTitle>
                
                <AppTextField
                  value={name}
                  onChange={handleNameChange}
                  label={lang === 'ko' ? "이름" : "Name"}
                  placeholder={lang === 'ko' ? "이름을 입력해주세요" : "Please enter your name"}
                  errorMessage={name && !isNameValid ? texts.nameValidationError : ''}
                  autoFocus={true}
                  autoComplete="name"
                />

                <AppGPhoneTextField
                  value={phone}
                  onChange={handlePhoneChange}
                  language={lang}
                />

                <AppDropdownField
                  value={country}
                  onChange={handleCountryChange}
                  options={countryOptions}
                  label={lang === 'ko' ? "국가" : "Country"}
                  placeholder={lang === 'ko' ? "국가를 선택해주세요" : "Please select country"}
                  errorMessage={country && !isCountryValid ? texts.countryValidationError : ''}
                  language={lang}
                />
                <PaymentGuideText>
                  <span style={{ fontSize: '14px', fontWeight: 'bold' }}>💡</span>
                  <span>{texts.paymentCurrencyGuide}</span>
                </PaymentGuideText>
              </FormGroup>

              <Agreement
                agreeContents={agreeContents}
                onAgreementChange={handleAgreementChange}
                initialAgreements={individualAgreements}
              />
            </ContentContainer>

            <CompleteButtonContainer>
              <PrimaryButton
                onClick={handleComplete}
                disabled={!isFormValid}
              >
                {lang === 'ko' ? "완료" : "Complete"}
              </PrimaryButton>
            </CompleteButtonContainer>
          </FlexPageContainer>
        </PageContainerLayout>

      {isTermsSheetOpen && (
        <TermsConsentSheet
          sections={termsSection}
          viewedSection={currentTermsSection}
          onClose={handleTermsSheetClose}
          onSectionClick={handleTermsSectionClick}
          isLoading={isLoadingTerms}
        />
      )}
      </FormContainer>
    </CenteredLayout>
  );
}

export default SignUpStep2;
